<template>
  <div class="input-coin-amount-root">
    <field
      v-slot="{ field, errors }"
      :name="name"
      :rules="rules"
      :model-value="formattedValue"
      validate-on-input
      @update:modelValue="onInput"
    >
      <div
        class="coin-amount-input"
        :class="{
          coinAmountInput__error: !!errors[0] || !!errorText,
        }"
      >
        <div class="coin-amount-input__title">
          {{ $t(title) }}
          <slot name="popoverWithLimits" />
        </div>

        <div class="coin-amount-input__count">
          <span v-if="balance !== null">{{ formatNumber(balance) }}</span>
          <button
            v-if="showMaxButton"
            type="button"
            class="coin-amount-input__count__btn"
            @click.self="onMaxClick"
          >
            {{ $t('InputCoinAmount | max') }}
          </button>
        </div>

        <div class="coin-amount-input__validator">
          <div class="coin-amount-input__validator__input">
            <input
              v-bind="field"
              ref="input"
              v-only-numbers.decimal
              class="validator__input"
              :class="{
                '!cursor-default': disabledInput,
              }"
              placeholder="0"
              autocomplete="off"
              :disabled="disabledInput"
              @focus="onFocus"
            />
            <div
              class="error-message"
              @click="getValueFromError(errors[0])"
              v-html="errors[0] || errorText"
            />
          </div>
        </div>

        <div class="coin-amount-input__select">
          <coin-dropdown
            class="coin-amount-input__select__custom"
            :model-value="coinId"
            :list="filteredFromList"
            :label="false"
            placeholder=""
            :show-active-balance="false"
            :without-coin-balance="coinDropdownWithoutCoinBalance"
            @update:modelValue="updateCoin"
          />
        </div>
      </div>
    </field>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import { Field } from 'vee-validate'
import { formatNumber } from '@/shared/lib/formatNumber'
import { CoinDropdown } from '@/features/CoinDropdown'

export default {
  components: {
    Field,
    CoinDropdown,
  },

  props: {
    // Unique name for the form allows VeeValidate distinguish different inputs
    name: {
      type: String,
    },
    // Current input value (from v-model)
    modelValue: {
      type: String,
    },
    // Kind of a label inside the input component
    title: {
      type: String,
      default: '',
    },
    // Current selected coin id
    coinId: {
      type: String,
    },
    // Object with any keys. Values should be coin/config descriptions of the coins
    coinList: {
      type: Object,
      default: null,
    },
    // String with validation rules for Veevalidate
    rules: {
      type: String,
      default: null,
    },
    // Min limit for the number in input (in the current coin)
    limitMin: {
      type: Number,
      default: 0,
    },
    // Max limit for the number in input (in the current coin)
    limitMax: {
      type: Number,
      default: Infinity,
    },
    // Balance of the user in the selected coin
    balance: {
      type: Number,
      default: null,
    },
    // Should we show the Max button
    showMaxButton: {
      type: Boolean,
      default: false,
    },
    // Should we focus on the input when the component is mounted
    focusWhenMounted: {
      type: Boolean,
      default: false,
    },
    // Flag - should we show an icon with the popover about limits
    limitIcon: {
      type: Boolean,
      default: true,
    },
    coinDropdownWithoutCoinBalance: {
      type: Boolean,
      default: false,
    },
    disabledInput: {
      type: Boolean,
      default: false,
    },
    errorText: {
      type: [String, null],
      default: null,
    },
  },

  computed: {
    ...mapState('coinService', ['coins']),
    ...mapGetters('coinService', ['transformeredBalances']),

    formattedValue() {
      return String(this.modelValue)
    },

    filteredFromList() {
      return Object.values(this.coinList).map((item) => ({
        coin: {
          ...item,
        },
        text: item.id,
        value: item.id,
        coin_id: item.id,
        wallet_balance: this.transformeredBalances.find(
          (balance) => balance.coin_id === item.id
        )?.wallet_balance,
      }))
    },
  },

  methods: {
    formatNumber,

    // When user focus on the input to fulfill amount
    onFocus() {
      this.$emit('focus')
    },

    // When user input a new sum in the field
    onInput(inputValue) {
      if (inputValue === '0.') {
        this.$emit('update:modelValue', '')
        return
      }

      // If the input value is ".", change it to "0."
      if (
        inputValue === '.' ||
        inputValue === '0' ||
        inputValue.match(/^0[0-9].*$/)
      ) {
        this.$emit('update:modelValue', '0.')
        return
      }

      this.$emit('update:modelValue', inputValue)
    },

    // When user selected another coin
    updateCoin(value) {
      this.$emit('update:coin', value)
    },

    // When user click a max link to get the maximum available amount
    onMaxClick() {
      if (!Number.isFinite(this.limitMax)) return // check that this.limitMax isn't an Infinity
      // Double focus on the input to show the error[0] message if there is such message
      this.$refs.input.focus()
      this.$refs.input.blur()
      this.$refs.input.focus()

      // Tell outside that focus is here
      this.$emit('focus')

      // If max limit is zero (balance - fee <= 0) it's more user friendly to insert full user balance
      // Logic behind is - if it's possible to insert valid max value
      const newAmount = this.limitMax > 0 ? this.limitMax : this.balance
      this.$emit('update:modelValue', `${newAmount}`)
    },

    getValueFromError(error) {
      const match = error.match(
        /<span class='error-message-sum'>([\d,.]+).*<\/span>/
      )
      if (match) {
        const amount = +match[1]
        this.$emit('update:modelValue', `${amount}`)
      }
    },
  },

  watch: {
    // Workaround - to force validation if value changed from outside the component
    async value() {
      await this.$refs.validator.validate()
    },
  },
}
</script>

<style scoped lang="scss">
.coin-amount-input {
  height: 120px;
  background-color: var(--bg-1);
  border: 1px solid var(--bg-35);
  border-radius: 12px;
  padding: 20px;
  position: relative;
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-gap: 1rem;

  &__title {
    display: flex;
    gap: 5px;
    align-items: center;
    font-size: 14px;
    font-weight: 500;
    color: var(--text-3);
  }

  &__count {
    display: flex;
    justify-content: end;
    align-self: center;
    font-size: 14px;
    font-weight: 500;
    color: var(--text-3);

    &__btn {
      color: var(--brand);
      font-size: 14px;
      font-weight: 500;
      margin-left: 10px;
    }

    &__btn:hover {
      color: var(--brand-hover);
    }
  }

  &__validator {
    height: 48px;
    &__input {
      input {
        outline: none;
        background-color: var(--bg-1);
        font-size: 20px;
        font-weight: 700;
        display: block;
        width: 100%;
        color: var(--text-1);
        min-height: 26px;
      }

      input:hover {
        cursor: pointer;
      }

      input::placeholder {
        color: var(--text-4);
        font-size: 20px;
        font-weight: 700;
      }
    }
  }

  &__select {
    display: flex;
    justify-content: end;

    :deep(.coin-amount-input__select__custom) {
      .coin-dropdown {
        position: static;

        .base-select--activator {
          background-color: var(--bg-35);
          height: 36px;
          gap: 0;
          padding: 6px 10px;
          min-width: 110px;

          .coin-dropdown__main {
            min-width: 40px;
            margin-left: 5px;
          }
        }

        .base-select--activator:hover {
          background-color: var(--bg-4);
        }

        .base-select--main {
          left: 5%;
          right: 21px;
          top: 100px;
          z-index: 70;
          max-height: 310px;

          ul {
            max-height: calc(310px - 60px);
          }
        }
      }
    }
  }
}

.coin-amount-input:focus-within {
  border: 1px solid var(--brand);
}

.coin-amount-input.coinAmountInput__error {
  border: 1px solid var(--error);
}

@media (max-width: 768px) {
  .coin-amount-input {
    &__select {
      :deep(.coin-amount-input__select__custom) {
        .coin-dropdown {
          .base-select--activator {
            height: 36px;
            padding-left: 8px;
            padding-right: 12px;

            .coin-dropdown__main-icon {
              width: 20px;
            }
          }
          .base-select--main {
            width: auto;
          }
        }
      }
    }
  }
}

@media (max-width: 480px) {
  .coin-amount-input {
    &__select {
      :deep(.coin-amount-input__select__custom) {
        .coin-dropdown {
          .base-select--main {
            width: auto;
            left: 5%;
          }
        }
      }
    }
  }
}

.error-message {
  font-size: 12px;
  font-weight: 500;
  color: var(--error);
}
</style>

<style>
.error-message-sum {
  text-decoration-line: underline;
  cursor: pointer;
}
</style>
